/**
 * Paged member list of a big group (more than BIG_GROUP_MIN wallets). The group stays one card on the
 * canvas; this list is how the officer reads every member without 366 nodes landing on the picture.
 * A row can be pinned: that single wallet is then drawn on the canvas beside the card.
 */
import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { ORIGIN_CLASSES, type MemberRow, type OriginClass } from './fusionMacro';

export const MEMBER_PAGE_SIZE = 25;

const shortAddr = (a: string) => (a.length > 16 ? `${a.slice(0, 6)}…${a.slice(-6)}` : a);

export function GroupMemberList({ title, rows, pinned, onPin, onClose }: {
  title: string;
  rows: MemberRow[];
  pinned: ReadonlySet<string>;
  onPin: (id: string) => void;
  onClose: () => void;
}) {
  const [filter, setFilter] = useState<OriginClass | 'all'>('all');
  const [page, setPage] = useState(0);
  const counts = useMemo(() => {
    const c: Partial<Record<OriginClass, number>> = {};
    for (const r of rows) c[r.origin] = (c[r.origin] || 0) + 1;
    return c;
  }, [rows]);
  const shown = useMemo(() => (filter === 'all' ? rows : rows.filter((r) => r.origin === filter)), [rows, filter]);
  const pages = Math.max(1, Math.ceil(shown.length / MEMBER_PAGE_SIZE));
  useEffect(() => { setPage(0); }, [filter, rows]);
  const slice = shown.slice(page * MEMBER_PAGE_SIZE, (page + 1) * MEMBER_PAGE_SIZE);
  const from = shown.length ? page * MEMBER_PAGE_SIZE + 1 : 0;
  const to = Math.min(shown.length, (page + 1) * MEMBER_PAGE_SIZE);

  return (
    <div
      className="absolute right-3 top-14 bottom-3 z-30 flex w-80 flex-col rounded-lg border border-border bg-card text-xs shadow-lg"
      role="dialog" aria-label={`Members of ${title}`} data-testid="group-member-list"
    >
      <div className="flex items-start justify-between gap-2 border-b border-border px-3 py-2">
        <div className="min-w-0">
          <div className="truncate font-semibold" data-testid="group-member-title">{title}</div>
          <div className="text-[10px] text-muted-foreground">{rows.length} wallets, listed here instead of drawn</div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close member list" data-testid="group-member-close"
          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex flex-wrap gap-1 border-b border-border px-3 py-2" role="group" aria-label="Filter by origin">
        <button type="button" onClick={() => setFilter('all')} aria-pressed={filter === 'all'} data-testid="group-filter-all"
          className={`rounded-full border px-2 py-0.5 ${filter === 'all' ? 'border-primary bg-primary/10' : 'border-border'}`}>
          all {rows.length}
        </button>
        {ORIGIN_CLASSES.filter((c) => (counts[c.key] || 0) > 0).map((c) => (
          <button key={c.key} type="button" onClick={() => setFilter(c.key)} aria-pressed={filter === c.key}
            data-testid={`group-filter-${c.key}`}
            className={`rounded-full border px-2 py-0.5 ${filter === c.key ? 'border-primary bg-primary/10' : 'border-border'}`}>
            {c.label} {counts[c.key]}
          </button>
        ))}
      </div>
      <ul className="min-h-0 flex-1 divide-y divide-border overflow-y-auto" data-testid="group-member-rows">
        {slice.map((r) => (
          <li key={r.id} className="flex items-center gap-2 px-3 py-1.5" data-member-id={r.id}>
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">{r.label}</div>
              <div className="truncate font-mono text-[10px] text-muted-foreground">
                {r.address ? shortAddr(r.address) : r.id} · {ORIGIN_CLASSES.find((c) => c.key === r.origin)?.label}
              </div>
            </div>
            <button type="button" onClick={() => onPin(r.id)} disabled={pinned.has(r.id)}
              data-testid={`group-pin-${r.id}`}
              className="shrink-0 rounded border border-border px-1.5 py-0.5 hover:bg-muted disabled:opacity-50">
              {pinned.has(r.id) ? 'On canvas' : 'Show on canvas'}
            </button>
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-between border-t border-border px-3 py-1.5">
        <span data-testid="group-member-range">{from}–{to} of {shown.length}</span>
        <span className="flex gap-1">
          <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
            data-testid="group-page-prev" className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40">Prev</button>
          <button type="button" onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1}
            data-testid="group-page-next" className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40">Next</button>
        </span>
      </div>
    </div>
  );
}
